import { useEffect, useState } from "react";
import { Flame, X, Skull } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * "Ashen Threshold" — the emotional-investment upsell.
 *
 * Shown right after a player dies IF they had a meaningful run (killed >=5 foes
 * OR reached floor >=3). Catches whales at peak sunk-cost feeling with a hot
 * "revive & continue" pitch BEFORE the generic Lives-Exhausted flow.
 *
 * Two products offered:
 *  - Second Wind (revive same run at half HP): $0.99, product_key="second_wind"
 *  - 5 Embers (best value pack, keeps them alive for days): $7.99, product_key="pack_five"
 *
 * On dismiss, the player falls through to the normal GameOverScreen flow.
 */
export default function AshenThresholdModal({ open, run, onClose }) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      // Play a subtle "ember flare" sting so the modal feels earned, not spammy.
      try {
        const a = new Audio("https://incompetech.com/music/royalty-free/mp3-royaltyfree/Bump%20in%20the%20Night.mp3");
        a.volume = 0.15;
        a.play().catch(() => { /* autoplay blocked — silently ignore */ });
        return () => { try { a.pause(); } catch (_e) { /* audio already gone */ } };
      } catch (_err) { /* Audio constructor unavailable — ignore */ }
    }
  }, [open]);

  if (!open || !run) return null;

  const floor = run.floor || 1;
  const kills = run.total_kills || run.kills || 0;
  const gold = run.gold || 0;
  const chapterName = run.chapter_title || `Floor ${floor}`;

  const buy = async (product_key) => {
    setBusy(true);
    try {
      const res = await api.post("/store/checkout", {
        product_key,
        origin_url: window.location.origin,
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("The Merchant took your offering but gave no path back.");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Merchant turns away.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(120,20,10,0.55) 0%, rgba(6,4,3,0.92) 65%)",
        backdropFilter: "blur(6px)",
      }}
      data-testid="ashen-threshold-modal"
    >
      <div
        className="relative w-full max-w-2xl panel border-2 p-8 md:p-10"
        style={{
          borderColor: "rgba(214,158,71,0.5)",
          boxShadow:
            "0 0 60px rgba(217,101,52,0.35), inset 0 0 30px rgba(0,0,0,0.55)",
        }}
      >
        <button
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 opacity-50 hover:opacity-100 transition"
          data-testid="ashen-close-btn"
          aria-label="Dismiss"
        >
          <X size={20} />
        </button>

        {/* Header — sunk-cost framing */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 blood-text text-xs tracking-[0.4em] mb-4">
            <Skull size={14} />
            <span>THE ASHEN THRESHOLD</span>
            <Skull size={14} />
          </div>
          <h2
            className="font-heading text-4xl md:text-5xl"
            style={{ color: "#e6c07a", letterSpacing: "0.02em" }}
          >
            You almost broke through.
          </h2>
          <p className="ash-text mt-4 text-sm md:text-base italic leading-relaxed max-w-lg mx-auto">
            You struck {kills > 0 ? <span className="ember font-bold">{kills} foe{kills === 1 ? "" : "s"}</span> : "the dark"} and reached
            {" "}<span className="gold-text font-bold">{chapterName}</span>.
            The Ember still remembers your name. Will you let it fade?
          </p>
        </div>

        {/* Two-column offers */}
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          {/* Second Wind — targeted, cheap */}
          <button
            onClick={() => buy("second_wind")}
            disabled={busy}
            className="group relative panel p-5 text-left border transition-all hover:scale-[1.02]"
            style={{
              borderColor: "rgba(217,101,52,0.4)",
              background: "linear-gradient(135deg, rgba(50,15,10,0.6), rgba(20,10,8,0.6))",
            }}
            data-testid="ashen-second-wind-btn"
          >
            <div className="flex items-center gap-2 text-xs tracking-[0.3em] blood-text mb-2">
              <Flame size={14} />
              SECOND WIND
            </div>
            <div className="font-heading text-3xl ember">$0.99</div>
            <div className="mt-2 text-sm text-[#f0e6d2]">
              Rise now, this run, at <span className="gold-text">60/100 HP</span>.
              Keep your gold, your gear, your progress.
            </div>
            <div className="mt-3 text-[10px] tracking-[0.2em] ash-text">
              ONE-TIME · SAME RUN · INSTANT
            </div>
          </button>

          {/* 5 Embers pack — the whale option */}
          <button
            onClick={() => buy("pack_five")}
            disabled={busy}
            className="group relative panel p-5 text-left border-2 transition-all hover:scale-[1.02]"
            style={{
              borderColor: "rgba(214,158,71,0.7)",
              background:
                "linear-gradient(135deg, rgba(90,60,15,0.55), rgba(20,10,8,0.75))",
              boxShadow: "0 0 24px rgba(214,158,71,0.25)",
            }}
            data-testid="ashen-pack-five-btn"
          >
            <div className="absolute -top-2 right-4 px-2 py-0.5 text-[9px] tracking-[0.3em] gold-text bg-black/80 border border-[#d69e47]/60 rounded">
              BEST VALUE
            </div>
            <div className="flex items-center gap-2 text-xs tracking-[0.3em] gold-text mb-2">
              <Flame size={14} />
              FIVE EMBERS
            </div>
            <div className="font-heading text-3xl ember">$7.99</div>
            <div className="mt-2 text-sm text-[#f0e6d2]">
              Five new lives. Descend farther. Master the deeper floors.
              Come back tomorrow with embers to spare.
            </div>
            <div className="mt-3 text-[10px] tracking-[0.2em] gold-text">
              5 LIVES · STACKS WITH DAILY · $1.60 EACH
            </div>
          </button>
        </div>

        {/* Escape hatch — subtle */}
        <div className="mt-8 text-center">
          <button
            onClick={onClose}
            disabled={busy}
            className="text-xs tracking-[0.3em] ash-text hover:text-[#f0e6d2] transition"
            data-testid="ashen-let-fade-btn"
          >
            LET THE EMBER FADE
          </button>
          <p className="text-[10px] tracking-[0.2em] ash-text/60 mt-2">
            SECURE CHECKOUT · POWERED BY STRIPE
          </p>
        </div>
      </div>
    </div>
  );
}
